import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { useAgentContext } from '../../context/agent';
import { useMutation, useQueryClient } from 'react-query';
import { addFaq, updateFaq } from '../../api/agent';
import { FaQuestionCircle, FaPlus, FaTimes, FaExclamationTriangle, FaEdit } from 'react-icons/fa';
import { IFAQ } from '../../models/faq';

type Props = {
    qn?: string;
    show: boolean;
    setShow: React.Dispatch<React.SetStateAction<boolean>>;
    faq?: IFAQ;
}

const ManageFAQ = ({show, setShow, qn, faq} : Props) => {
    const [question, setQuestion] = useState(qn || "");
    const [answer, setAnswer] = useState("");
    const [error, setError] = useState<string | null>(null);
    
    const isEditMode = !!faq;

    const {agentId} = useParams();
    const {getAgentById} = useAgentContext();
    const agent = getAgentById(agentId || "");

    // Initialize form fields when FAQ is provided for editing
    useEffect(() => {
        if (faq) {
            setQuestion(faq.question);
            setAnswer(faq.answer);
        } else {
            setQuestion(qn || "");
            setAnswer("");
        }
        setError(null);
    }, [faq, qn]);

    const handleClose = () => {
        setShow(false);
        setError(null);
        if (!isEditMode) {
            setQuestion(qn || "");
            setAnswer("");
        }
    };

    const queryClient = useQueryClient();
    
    const { mutateAsync: addFaqFn, isLoading: isAdding } = useMutation(
        addFaq,
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['Organizations']);
                queryClient.invalidateQueries(['Faqs', agent?.id]);
                handleClose();
            },
            onError: (error: any) => {
                const errorMessage = error?.response?.data?.message || "Failed to add FAQ. Please try again.";
                setError(errorMessage);
            },
        }
    );

    const { mutateAsync: updateFaqFn, isLoading: isUpdating } = useMutation(
        updateFaq,
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['Organizations']);
                queryClient.invalidateQueries(['Faqs', agent?.id]);
                handleClose();
            },
            onError: (error: any) => {
                const errorMessage = error?.response?.data?.message || "Failed to update FAQ. Please try again.";
                setError(errorMessage);
            },
        }
    );

    const handleSubmit = async () => {
        if(!question?.trim() || !answer?.trim()) {
            setError('Both question and answer are required.');
            return;
        }
        
        if(!agent) {
            setError("Unexpected error. Please reload page and retry.");
            return;
        }

        try {
            if (isEditMode && faq) {
                await updateFaqFn({
                    orgId: agent.organizationId,
                    agentId: agent.id,
                    id: faq.id,
                    question: question.trim(),
                    answer: answer.trim(),
                });
            } else {
                await addFaqFn({
                    agentId: agent.id,
                    orgId: agent.organizationId,
                    question: question.trim(),
                    answer: answer.trim(),
                });
            }
        } catch (err) {
            // Error handling is done in the mutation's onError callback
        }
    };

    const isLoading = isAdding || isUpdating;

    return (
        <Modal 
            show={show} 
            onHide={handleClose}
            size="lg"
            contentClassName="bg-dark border-secondary"
        >
            <Modal.Header 
                closeButton
                className="border-secondary"
                style={{ backgroundColor: "#2d2d2d" }}
            >
                <div className="d-flex align-items-center">
                    <div 
                        className="me-3 d-flex align-items-center justify-content-center"
                        style={{
                            width: "32px",
                            height: "32px",
                            backgroundColor: "#ffc107",
                            borderRadius: "8px"
                        }}
                    >
                        <FaQuestionCircle className="text-white" size={16} />
                    </div>
                    <Modal.Title className="text-light mb-0" style={{ fontSize: "18px", fontWeight: "600" }}>
                        {isEditMode ? "Edit FAQ" : "Add FAQ"}
                    </Modal.Title>
                </div>
            </Modal.Header>
            
            <Modal.Body style={{ backgroundColor: "#1a1a1a" }}>
                <Form>
                    <Form.Group className="mb-4">
                        <Form.Label className="text-light fw-semibold mb-2">
                            Question
                        </Form.Label>
                        <Form.Text className="d-block mb-3" style={{ color: "#ccc" }}>
                            Enter the frequently asked question that customers might have.
                        </Form.Text>
                        <Form.Control
                            id='question'
                            type='text'
                            value={question}
                            onChange={(e) => setQuestion(e.target.value)}
                            className="bg-dark text-light border-secondary mb-4"
                            style={{ 
                                backgroundColor: "#2d2d2d",
                                borderColor: "#555",
                                color: "#fff"
                            }}
                            placeholder="What is your return policy?"
                            disabled={isLoading}
                        />
                        
                        <Form.Label className="text-light fw-semibold mb-2">
                            Answer
                        </Form.Label>
                        <Form.Text className="d-block mb-3" style={{ color: "#ccc" }}>
                            Provide a clear and helpful answer to this question.
                        </Form.Text>
                        <Form.Control
                            id='answer'
                            as="textarea" 
                            rows={6}
                            value={answer}
                            onChange={(e) => setAnswer(e.target.value)}
                            className="bg-dark text-light border-secondary"
                            style={{ 
                                backgroundColor: "#2d2d2d",
                                borderColor: "#555",
                                color: "#fff",
                                resize: "vertical"
                            }}
                            placeholder="We offer a 30-day return policy for all unused items..."
                            disabled={isLoading}
                        />
                    </Form.Group>
                </Form>
                
                {error && (
                    <Alert 
                        variant="danger" 
                        className="mt-3"
                        style={{ 
                            backgroundColor: "#2d1a1a",
                            borderColor: "#dc3545",
                            color: "#ff6b6b"
                        }}
                    >
                        <div className="d-flex align-items-center">
                            <FaExclamationTriangle className="me-2" />
                            {error}
                        </div>
                    </Alert>
                )}
            </Modal.Body>
            
            <Modal.Footer 
                className="border-secondary"
                style={{ backgroundColor: "#2d2d2d" }}
            >
                <Button 
                    variant="outline-secondary" 
                    onClick={handleClose}
                    className="d-flex align-items-center"
                    style={{ borderColor: "#555", color: "#ccc" }}
                    disabled={isLoading}
                >
                    <FaTimes className="me-2" size={14} />
                    Cancel
                </Button>
                <Button 
                    variant="warning" 
                    onClick={handleSubmit}
                    disabled={isLoading || !question?.trim() || !answer?.trim()}
                    className="d-flex align-items-center"
                    style={{ 
                        backgroundColor: "#ffc107",
                        borderColor: "#ffc107"
                    }}
                >
                    {isLoading ? (
                        <>
                            <Spinner size="sm" className="me-2" />
                            {isEditMode ? "Updating..." : "Adding..."}
                        </>
                    ) : (
                        <>
                            {isEditMode ? (
                                <FaEdit className="me-2" size={14} />
                            ) : (
                                <FaPlus className="me-2" size={14} />
                            )}
                            {isEditMode ? "Update FAQ" : "Add FAQ"}
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ManageFAQ;